import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import * as yup from 'yup';
import {
    LocationProfilesDictionary,
    ProfileName,
    isListingCoveredByOfficial,
    toLocationProfileType,
} from '@constants/integrations';
import { alertActions, accountActions, locationsProfilesActions } from '@actions';
import { validateUrl } from '@helpers/connect-integration';
import { companyService } from '@services';
import Drawer from '@components/Drawer';
import { Select } from '@components/Select';
import { Input } from '@components/Input';
import { handleApiError } from '@helpers/api';
import { useTranslation, useYupValidationResolver } from '@hooks';
import GoogleProfile from './GoogleProfile';

export const PROFILE_MODE = { ADD: 0, EDIT: 1 };

const bookingExtranetIdPattern = /^\d{1,12}$/;
const bookingExtranetUrlPattern = /^https?:\/\/([\w-]+\.)*booking\.com\/[^\s]*[?&]hotel_id=\d{1,12}(?=[&#]|$)/i;

const PROFILE_URL_HINTS = {
    [ProfileName.UberEats]: 'SettingsCompanies.uberEatsReviewsHint',
};

const GeneralConnect = ({ profile, setProfile, locationsProfiles, selectedCompany, setSelectedCompany }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const profilesOptions = LocationProfilesDictionary.filter(p => {
        if (profile.mode === PROFILE_MODE.ADD) {
            if (isListingCoveredByOfficial(locationsProfiles, p.type)) return false;
            return !locationsProfiles.some(
                sP => toLocationProfileType(sP.type) === p.type && sP.shareSource !== 'location',
            );
        }
        if (toLocationProfileType(profile.data?.type) === p.type) return true;
        return !locationsProfiles.some(sP => toLocationProfileType(sP.type) === p.type);
    }).map(p => ({ value: p.type, label: p.name }));

    const ProfileSchema = yup.object().shape(
        {
            profileUrl: yup
                .string()
                .required('validation.required')
                .test('test-url', 'validation.invalidURL', (value, ctx) => {
                    if (+ctx.parent.profileType === ProfileName.Google) return true;
                    return validateUrl(value, ctx.parent.profileType);
                }),
            manageUrl: yup.string().test('test-extranet-url', 'validation.invalidURL', (value, ctx) => {
                if (+ctx.parent.profileType !== ProfileName['Booking.com'] || !value) return true;
                return bookingExtranetIdPattern.test(value.trim()) || bookingExtranetUrlPattern.test(value.trim());
            }),
            profileType: yup.string().required('validation.required'),
        },
        [['profileType', 'profileType']],
    );

    const resolver = useYupValidationResolver(ProfileSchema);

    const {
        getValues,
        handleSubmit,
        control,
        watch,
        setValue,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm({
        mode: 'all',
        resolver: resolver,
        defaultValues: {
            profileUrl: '',
            manageUrl: '',
            profileType:
                profile.mode === PROFILE_MODE.ADD
                    ? profilesOptions[0]?.value
                    : toLocationProfileType(profile.data?.type),
        },
    });

    useEffect(() => {
        if (profile.mode === PROFILE_MODE.EDIT) {
            setValue('profileUrl', profile.data.businessUrl, { shouldValidate: true });
            setValue('manageUrl', profile.data.manageUrl || '', { shouldValidate: true });
        }
    }, [profile]);

    const onSubmit = async () => {
        const { profileUrl, profileType, manageUrl } = getValues();
        try {
            const updatedCompany = await companyService.editProfile({
                locationId: selectedCompany?.companyId,
                type: profileType,
                url: profileUrl,
                manageUrl: +profileType === ProfileName['Booking.com'] ? manageUrl : undefined,
            });
            setSelectedCompany(updatedCompany);
            await dispatch(locationsProfilesActions.get(selectedCompany.companyId));
            await dispatch(accountActions.get());
            dispatch(alertActions.success(t('SettingsCompanies.connectIntegrationMsg')));
            setProfile(prev => ({ ...prev, open: false, data: null, mode: PROFILE_MODE.ADD }));
        } catch (error) {
            handleApiError(error);
        }
    };

    const SELECTED_PROFILE = watch('profileType');
    const urlHintKey = PROFILE_URL_HINTS[SELECTED_PROFILE];

    return (
        <Drawer
            aboveModal
            anchor="right"
            open={profile.open}
            onClose={() => setProfile(prev => ({ ...prev, open: false, data: null, mode: PROFILE_MODE.ADD }))}
            title={t(
                profile.mode === PROFILE_MODE.ADD
                    ? 'SettingsCompanies.addReviewPlatform'
                    : 'SettingsCompanies.editProfile',
            )}
            primaryText={t('buttons.connect')}
            primaryDisabled={isSubmitting || !isValid || !isDirty}
            primaryAction={handleSubmit(onSubmit)}
        >
            <Controller
                control={control}
                name="profileType"
                render={({ field, ref }) => (
                    <Select
                        {...field}
                        ref={ref}
                        label={t('common.platform')}
                        fullWidth
                        options={profilesOptions}
                        disabled={profile.mode === PROFILE_MODE.EDIT}
                        style={{ marginBottom: '8px' }}
                    />
                )}
            />
            {SELECTED_PROFILE !== ProfileName.Google ? (
                <Controller
                    control={control}
                    name="profileUrl"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field }}
                            label={t('SettingsCompanies.profileURL')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : urlHintKey ? t(urlHintKey) : null}
                            required
                        />
                    )}
                />
            ) : (
                <FormProvider {...{ setValue }}>
                    <Controller control={control} name="profileUrl" render={() => <GoogleProfile />} />
                </FormProvider>
            )}
            {+SELECTED_PROFILE === ProfileName['Booking.com'] && (
                <div style={{ marginTop: '8px' }}>
                    <Controller
                        control={control}
                        name="manageUrl"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{ ...field }}
                                label={t('SettingsCompanies.bookingExtranetLink')}
                                fullWidth
                                error={!!error}
                                helperText={error ? t(error.message) : t('SettingsCompanies.bookingExtranetHint')}
                            />
                        )}
                    />
                </div>
            )}
        </Drawer>
    );
};

export default GeneralConnect;
